'use client';

import { useState, useEffect, useMemo } from 'react';
import { PageJSON, Section, TransformedNewsItem } from '@/lib/types/pageBuilder';
import { DynamicTabInfo } from './TabBarSection';
import { IncludeSectionApiData } from '@/lib/pageUtils';
import PageRenderer from './PageRenderer';
interface ProgressivePageRendererProps {
  pageData: PageJSON;
  headingApiData?: Record<string, unknown>;
  // Pre-fetched data from server (for faster initial render)
  prefetchedCollections?: Record<string, TransformedNewsItem[]>;
  prefetchedDynamicTabs?: Record<string, DynamicTabInfo[]>;
  prefetchedIncludeSections?: Record<string, Section>;
  prefetchedSectionIndices?: number[];
}
interface SectionLoadState {
  sectionIndex: number;
  loading: boolean;
  loaded: boolean;
  error: boolean;
}
export default function ProgressivePageRenderer({
  pageData,
  headingApiData,
  prefetchedCollections,
  prefetchedDynamicTabs,
  prefetchedIncludeSections,
  prefetchedSectionIndices = []
}: ProgressivePageRendererProps) {
  // Convert pre-fetched data to Maps
  const initialCollections = useMemo(() => {
    if (prefetchedCollections) {
      return new Map(Object.entries(prefetchedCollections));
    }
    return new Map<string, TransformedNewsItem[]>();
  }, [prefetchedCollections]);
  const initialDynamicTabs = useMemo(() => {
    if (prefetchedDynamicTabs) {
      return new Map(Object.entries(prefetchedDynamicTabs));
    }
    return undefined;
  }, [prefetchedDynamicTabs]);
  const initialIncludeSections = useMemo(() => {
    if (prefetchedIncludeSections) {
      return new Map(Object.entries(prefetchedIncludeSections));
    }
    return undefined;
  }, [prefetchedIncludeSections]);
  const [collectionsData, setCollectionsData] = useState<Map<string, TransformedNewsItem[]>>(initialCollections);
  const [dynamicTabsMap, setDynamicTabsMap] = useState<Map<string, DynamicTabInfo[]> | undefined>(initialDynamicTabs);
  const [includeSectionsData, setIncludeSectionsData] = useState<Map<string, Section> | undefined>(initialIncludeSections);
  const [includeSectionApiData, setIncludeSectionApiData] = useState<IncludeSectionApiData | undefined>(undefined);
  const totalSections = pageData?.sections?.length || 0;

  // Number of sections to load in parallel if not pre-fetched
  const PARALLEL_LOAD_COUNT = 3;

  // How many sections were pre-fetched
  const prefetchedCount = prefetchedSectionIndices.length;

  // Initialize loading states - mark pre-fetched sections as already loaded
  const [loadingStates, setLoadingStates] = useState<SectionLoadState[]>(() => {
    if (totalSections > 0) {
      return Array.from({
        length: totalSections
      }, (_, index) => {
        const isPrefetched = prefetchedSectionIndices.includes(index);
        return {
          sectionIndex: index,
          loading: !isPrefetched && index < prefetchedCount + PARALLEL_LOAD_COUNT,
          loaded: isPrefetched,
          // Pre-fetched sections are already loaded
          error: false
        };
      });
    }
    return [];
  });

  // Start loading from after pre-fetched sections
  const [currentSectionIndex, setCurrentSectionIndex] = useState(Math.max(prefetchedCount, PARALLEL_LOAD_COUNT));
  const [initialLoadDone, setInitialLoadDone] = useState(prefetchedCount > 0); // Skip initial load if we have pre-fetched data

  // Helper function to load a single section
  const loadSection = async (sectionIndex: number) => {
    const section = pageData.sections[sectionIndex];
    try {
      const response = await fetch('/api/section-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          section,
          pageData
        })
      });
      if (!response.ok) throw new Error('Failed to fetch section data');
      const result = await response.json();
      if (!result.success || !result.data) throw new Error('Invalid response');
      return {
        sectionIndex,
        data: result.data,
        error: false
      };
    } catch (error) {
      console.error(`Error loading section ${sectionIndex}:`, error);
      return {
        sectionIndex,
        data: null,
        error: true
      };
    }
  };

  // Merge section data into state
  const mergeSectionData = (sectionData: any) => {
    if (sectionData.collectionsData) {
      setCollectionsData(prev => {
        const newMap = new Map(prev);
        Object.entries(sectionData.collectionsData).forEach(([key, value]) => {
          newMap.set(key, value as TransformedNewsItem[]);
        });
        return newMap;
      });
    }
    if (sectionData.dynamicTabsMap) {
      setDynamicTabsMap(prev_0 => {
        const newMap_0 = new Map(prev_0 || new Map());
        Object.entries(sectionData.dynamicTabsMap).forEach(([key_0, value_0]) => {
          newMap_0.set(key_0, value_0 as DynamicTabInfo[]);
        });
        return newMap_0;
      });
    }
    if (sectionData.includeSectionsData) {
      setIncludeSectionsData(prev_1 => {
        const newMap_1 = new Map(prev_1 || new Map());
        Object.entries(sectionData.includeSectionsData).forEach(([key_1, value_1]) => {
          newMap_1.set(key_1, value_1 as Section);
        });
        return newMap_1;
      });
    }
  };

  // Load first N non-prefetched sections in PARALLEL
  useEffect(() => {
    if (!pageData?.sections || initialLoadDone) return;
    const loadInitialSections = async () => {
      // Find sections that need loading (not pre-fetched)
      const sectionsToLoad: number[] = [];
      for (let i = 0; i < totalSections && sectionsToLoad.length < PARALLEL_LOAD_COUNT; i++) {
        if (!prefetchedSectionIndices.includes(i)) {
          sectionsToLoad.push(i);
        }
      }
      if (sectionsToLoad.length === 0) {
        setInitialLoadDone(true);
        return;
      }
      const promises = sectionsToLoad.map(i_0 => loadSection(i_0));

      // Load all initial sections in parallel
      const results = await Promise.all(promises);

      // Process results
      results.forEach(({
        sectionIndex: sectionIndex_0,
        data,
        error: error_0
      }) => {
        if (data) mergeSectionData(data);
        setLoadingStates(prev_2 => prev_2.map((state, idx) => idx === sectionIndex_0 ? {
          ...state,
          loading: false,
          loaded: !error_0,
          error: error_0
        } : state));
      });
      setInitialLoadDone(true);
    };
    loadInitialSections();
  }, [pageData, totalSections, initialLoadDone, prefetchedSectionIndices]);

  // Load remaining sections sequentially (after initial parallel load)
  useEffect(() => {
    if (!pageData?.sections || !initialLoadDone || currentSectionIndex >= totalSections) {
      return;
    }

    // Skip pre-fetched sections
    if (prefetchedSectionIndices.includes(currentSectionIndex)) {
      setCurrentSectionIndex(prev_3 => prev_3 + 1);
      return;
    }
    const loadNextSection = async () => {
      // Mark section as loading
      setLoadingStates(prev_4 => prev_4.map((state_0, idx_0) => idx_0 === currentSectionIndex ? {
        ...state_0,
        loading: true
      } : state_0));
      const {
        data: data_0,
        error: error_1
      } = await loadSection(currentSectionIndex);
      if (data_0) mergeSectionData(data_0);

      // Mark section as loaded/error
      setLoadingStates(prev_5 => prev_5.map((state_1, idx_1) => idx_1 === currentSectionIndex ? {
        ...state_1,
        loading: false,
        loaded: !error_1,
        error: error_1
      } : state_1));

      // Move to next section immediately (no delay)
      setCurrentSectionIndex(prev_6 => prev_6 + 1);
    };
    loadNextSection();
  }, [currentSectionIndex, pageData, totalSections, initialLoadDone, prefetchedSectionIndices]);

  // Render sections with loading states
  return <div className="page-content border-[0.1px] border-DCDCDC">
      {pageData?.sections?.map((section_0, index_0) => {
      const loadState = loadingStates[index_0];
      const isLoading = loadState?.loading;
      const isLoaded = loadState?.loaded;
      const hasError = loadState?.error;
      if (!loadState || !isLoading && !isLoaded && !hasError || isLoading) {
        // Section pending or loading - render nothing, will appear when ready
        return null;
      }
      if (hasError) {
        // Section failed to load - show error
        return <div key={section_0.id} className="section-error p-4">
              <div className="bg-red-50 border border-red-200 rounded p-4 text-center">
                <p className="text-red-600 text-sm">Failed to load section</p>
              </div>
            </div>;
      }

      // Section is loaded - render it
      return <div key={section_0.id} className="section-loaded">
            <PageRenderer pageData={{
          ...pageData,
          sections: [section_0] // Render only this section
        }} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} headingApiData={headingApiData} />
          </div>;
    })}
    </div>;
}